import { ReactNode, useState } from 'react';
import Slider from '@components/atoms/slider';

type Range = [number, number];

// The slider renders its pills through BodyRegular (Open Sans 16px), so the
// design's Poppins 600 13px pill with 4px 8px padding has to win with
// important variants. Both pills share this class, so they get no side margin
// (the 12px gap to the track sits on the track instead) and stay flush with
// the field's edges. The min width is left to the caller so it can fit the
// widest value it shows.
const PILL_CLASS_NAME =
  '!font-sans !text-[13px] !font-semibold !leading-5 !py-1 !px-2 !w-auto !mb-0 !rounded-lg !mx-0';

const toRange = (value: number[] | number): Range =>
  Array.isArray(value) ? [value[0], value[1]] : [value, value];

export type RangeSliderFieldProps = {
  name: string;
  label: string;
  min: number;
  max: number;
  step: number;
  value: Range;
  /** Fires on every move, for callers that filter live while dragging. */
  onChange?: (range: Range) => void;
  /** Fires once when the thumb is released or a key press settles. */
  onCommit?: (range: Range) => void;
  formatLabel?: (value: number) => string;
  /** A static Tailwind min width class, e.g. `!min-w-[54px]`. */
  pillMinWidthClassName?: string;
  /** Rendered at the right of the label row, e.g. preset buttons. */
  actions?: ReactNode;
  disabled?: boolean;
};

/**
 * A labelled two-thumb range slider with the current values in pills at
 * either end of the track. The shared look for range filters across the site.
 */
const RangeSliderField = ({
  name,
  label,
  min,
  max,
  step,
  value,
  onChange,
  onCommit,
  formatLabel,
  pillMinWidthClassName = '!min-w-[54px]',
  actions,
  disabled
}: RangeSliderFieldProps) => {
  // Holds the range while a thumb is moving so commit-only callers still see
  // the thumbs and pills track the drag. Cleared on commit, so if the caller
  // rejects the change (e.g. a paywall) the slider falls back to `value`.
  const [draft, setDraft] = useState<Range | null>(null);

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <label
          htmlFor={name}
          className="font-open-sans text-sm text-neutral-800"
        >
          {label}
        </label>
        {actions && (
          <span className="flex items-center gap-1.5">{actions}</span>
        )}
      </div>
      {/* The slider root is itself a flex row in fixedLabels mode and has no
          intrinsic width, so it must be told to fill this wrapper or the
          track collapses to nothing between the pills. The react-slider
          element is only as tall as its 4px track (the thumbs overflow it),
          so it gets the thumb height and the row centres on it, plus the 12px gap
          to each pill. */}
      <div className="flex h-[38px] items-center [&>div]:flex-1 [&>div]:items-center [&_.horizontal-slider]:mx-3 [&_.horizontal-slider]:h-5">
        <Slider
          variant="controlled"
          fixedLabels
          name={name}
          min={min}
          max={max}
          step={step}
          minStepsBetweenThumbs={0}
          disabled={disabled}
          value={draft ?? value}
          setValue={(next) => {
            const range = toRange(next);
            setDraft(range);
            onChange?.(range);
          }}
          onValueCommit={(next) => {
            setDraft(null);
            onCommit?.(toRange(next));
          }}
          formatLabel={formatLabel}
          pillClassName={`${PILL_CLASS_NAME} ${pillMinWidthClassName}`}
        />
      </div>
    </div>
  );
};

type SliderPresetButtonProps = {
  isActive: boolean;
  onClick: () => void;
  children: ReactNode;
  testId?: string;
};

/** A small toggle for the `actions` slot, e.g. a "quick set" window size. */
export const SliderPresetButton = ({
  isActive,
  onClick,
  children,
  testId
}: SliderPresetButtonProps) => (
  <button
    type="button"
    data-testid={testId}
    aria-pressed={isActive}
    onClick={onClick}
    className={`h-[22px] min-w-[28px] rounded-md border px-[7px] font-open-sans text-xs font-semibold ${
      isActive
        ? 'border-primary-600 bg-primary-600 text-white'
        : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
    }`}
  >
    {children}
  </button>
);

export default RangeSliderField;
